// ---------------------------------------------------------------------------
// Design System Export — serialization for project persistence & generation
//
// Phase 403-01: Creates the bridge between the 6-axis DesignSystemState
// (studio in-memory) and the JSON formats consumed by:
//   1. Project save/load — full serialization (axes + cssVars + fonts + hints)
//   2. cogo-validator app-loader — flat Record<string, string>
//
// oklch values pass through verbatim (no hsl wrapping) per D-12.
// All values originate from static preset maps in shadcn-presets.ts — no
// user-supplied strings reach CSS output.
// ---------------------------------------------------------------------------

import { CANONICAL_THEME, type AtomDefaults } from '@cogo/design-presets'
import { computeThemeTokens } from './theme-tokens'
import type { DesignSystemState } from './design-system-options'

// ---------------------------------------------------------------------------
// Semantic className hints — Tailwind class names that the generation pipeline
// should know are valid with the active design system. Used by the LLM prompt
// context to constrain class usage to semantically meaningful tokens.
// ---------------------------------------------------------------------------

export const SEMANTIC_CLASSNAME_HINTS: readonly string[] = [
  // Background colors
  'bg-background',
  'bg-foreground',
  'bg-primary',
  'bg-secondary',
  'bg-muted',
  'bg-accent',
  'bg-destructive',
  'bg-card',
  'bg-popover',
  // Text colors
  'text-foreground',
  'text-primary-foreground',
  'text-secondary-foreground',
  'text-muted-foreground',
  'text-accent-foreground',
  'text-destructive-foreground',
  'text-card-foreground',
  'text-popover-foreground',
  // Border colors
  'border-border',
  'border-input',
  // Ring
  'ring-ring',
  // Radius
  'rounded-sm',
  'rounded-md',
  'rounded-lg',
  'rounded-xl',
  // Font
  'font-sans',
  'font-mono',
] as const

export type StudioRendererTarget = 'web' | 'rn' | 'flutter'

export interface StudioDesignSystemComponentContract {
  type: string
  ontologyTarget: string
  rendererTargets: StudioRendererTarget[]
  allowedVariants: string[]
  allowedSizes: string[]
  slots: string[]
  interactionEvents: string[]
  requiredState: string[]
  tokenRefs: string[]
  rules: string[]
}

export interface StudioDesignSystemComponentContractSet {
  contractVersion: 'cogo-design-system-contract.v1'
  rendererTargets: StudioRendererTarget[]
  components: StudioDesignSystemComponentContract[]
  tokenRefs: {
    color: string[]
    typography: string[]
    spacing: string[]
    radius: string[]
    elevation: string[]
    motion: string[]
  }
  constraints: string[]
}

export interface StudioDesignSystemBlockAdmissionContract {
  designQualityGradeFloor: number
  retrievalQualityFloor: number
  requireRenderedEvidence: true
  requireOntologyConformance: true
  requireActionFlowForInteractiveBlocks: true
  requireSemanticTokens: true
  disallowRawStyleValues: true
  requireComponentCoverage: true
  requireStyleSeparation: true
  requireDesignSystemStyleInjection: true
  requireStyleProvenance: true
  maxVisualClassNameTokens: 0
  maxInlineStyleObjects: 0
  maxUnresolvedDesignAuthority: 0
}

export interface StudioDesignSystemContract {
  schemaVersion: '4.0'
  designSystemId: string
  componentContract: StudioDesignSystemComponentContractSet
  blockAdmission: StudioDesignSystemBlockAdmissionContract
}

// ---------------------------------------------------------------------------
// Full serialization — for studio project persistence & generation pipeline
// ---------------------------------------------------------------------------

export interface SerializedDesignSystem {
  /** The 6-axis state values — used to restore studioStore on load. */
  axes: DesignSystemState
  /** Flat CSS variable map (oklch values, no hsl wrapping). */
  cssVars: Record<string, string>
  /** Resolved radius value (e.g., "0.625rem"). */
  radius: string
  /** Font stacks by slot (sans, mono, heading). */
  fonts?: { sans?: string; mono?: string; heading?: string }
  /** Semantic Tailwind class names valid for this design system. */
  classNameHint: readonly string[]
  /** Coogo JSON component/runtime contract used to constrain ontology-driven generation. */
  designSystemContract: StudioDesignSystemContract
}

const RENDERER_TARGETS: StudioRendererTarget[] = ['web', 'rn', 'flutter']

const BLOCK_ADMISSION_CONTRACT: StudioDesignSystemBlockAdmissionContract = {
  designQualityGradeFloor: 90,
  retrievalQualityFloor: 85,
  requireRenderedEvidence: true,
  requireOntologyConformance: true,
  requireActionFlowForInteractiveBlocks: true,
  requireSemanticTokens: true,
  disallowRawStyleValues: true,
  requireComponentCoverage: true,
  requireStyleSeparation: true,
  requireDesignSystemStyleInjection: true,
  requireStyleProvenance: true,
  maxVisualClassNameTokens: 0,
  maxInlineStyleObjects: 0,
  maxUnresolvedDesignAuthority: 0,
}

const SPECIALIZED_COMPONENT_CONTRACTS: StudioDesignSystemComponentContract[] = [
  {
    type: 'container',
    ontologyTarget: 'uiox:component-container',
    rendererTargets: RENDERER_TARGETS,
    allowedVariants: ['default'],
    allowedSizes: ['default'],
    slots: ['children'],
    interactionEvents: [],
    requiredState: [],
    tokenRefs: ['uiox:token-spacing-4', 'ctok:background'],
    rules: ['Use explicit layout and spacing tokens instead of arbitrary positioning.'],
  },
  {
    type: 'card',
    ontologyTarget: 'uiox:component-card',
    rendererTargets: RENDERER_TARGETS,
    allowedVariants: ['default'],
    allowedSizes: ['default'],
    slots: ['header', 'content', 'footer', 'children'],
    interactionEvents: [],
    requiredState: [],
    tokenRefs: ['ctok:card', 'ctok:foreground', 'ctok:rounded-md', 'ctok:shadow-sm'],
    rules: ['Use semantic surface, border, radius, and elevation tokens for card composition.'],
  },
  {
    type: 'button',
    ontologyTarget: 'uiox:component-button',
    rendererTargets: RENDERER_TARGETS,
    allowedVariants: ['default', 'secondary', 'outline', 'ghost', 'destructive'],
    allowedSizes: ['sm', 'default', 'lg', 'icon'],
    slots: ['icon', 'label'],
    interactionEvents: ['tap'],
    requiredState: ['local', 'appData'],
    tokenRefs: ['ctok:primary', 'ctok:primary-foreground', 'uiox:token-spacing-2'],
    rules: ['Interactive buttons require event binding, ActionFlow steps, and trace evidence.'],
  },
  {
    type: 'input',
    ontologyTarget: 'uiox:component-input',
    rendererTargets: RENDERER_TARGETS,
    allowedVariants: ['default'],
    allowedSizes: ['sm', 'default', 'lg'],
    slots: ['label', 'control', 'helper', 'error'],
    interactionEvents: ['change', 'input'],
    requiredState: ['local'],
    tokenRefs: ['ctok:border', 'ctok:background', 'ctok:foreground'],
    rules: ['Inputs must bind canonical local state and project event.value through ActionFlow.'],
  },
  {
    type: 'text',
    ontologyTarget: 'uiox:component-text',
    rendererTargets: RENDERER_TARGETS,
    allowedVariants: ['body', 'heading', 'label', 'caption'],
    allowedSizes: ['sm', 'base', 'lg', 'xl', '2xl', '3xl'],
    slots: ['content'],
    interactionEvents: [],
    requiredState: [],
    tokenRefs: ['ctok:foreground', 'ctok:muted-foreground', 'ctok:font-sans'],
    rules: ['Text must use semantic typography roles and remain non-overlapping at mobile and desktop widths.'],
  },
  {
    type: 'data-table',
    ontologyTarget: 'uiox:component-data-table',
    rendererTargets: RENDERER_TARGETS,
    allowedVariants: ['default', 'compact', 'editable'],
    allowedSizes: ['default', 'dense'],
    slots: ['toolbar', 'header', 'row', 'cell', 'pagination'],
    interactionEvents: ['rowClick', 'cellChange', 'sortChange', 'pageChange'],
    requiredState: ['local', 'appData'],
    tokenRefs: ['ctok:card', 'ctok:border', 'ctok:muted-foreground', 'uiox:token-spacing-3'],
    rules: ['Editable tables require datasource, state, sort/pagination, and ActionFlow trace evidence.'],
  },
]

const SPECIALIZED_COMPONENT_CONTRACT_BY_TYPE = new Map(
  SPECIALIZED_COMPONENT_CONTRACTS.map((contract) => [contract.type, contract]),
)

const CANONICAL_ATOMS = CANONICAL_THEME.atoms as Record<string, AtomDefaults>

/**
 * The generation component surface is derived from the same canonical atom
 * registry that owns Theme V2 defaults. Specialized contracts add interaction
 * and slot semantics; they cannot narrow the canonical variants or sizes.
 */
export function buildCanonicalComponentContracts(): StudioDesignSystemComponentContract[] {
  const canonical = Object.entries(CANONICAL_ATOMS).map(([type, atom]) => {
    const specialized = SPECIALIZED_COMPONENT_CONTRACT_BY_TYPE.get(type)
    return {
      type,
      ontologyTarget: `uiox:component-${type}`,
      rendererTargets: specialized?.rendererTargets ?? RENDERER_TARGETS,
      allowedVariants: canonicalValues(
        atom.availableVariants,
        atom.defaultVariant,
      ),
      allowedSizes: canonicalValues(atom.availableSizes, atom.defaultSize),
      slots: specialized?.slots ?? ['root'],
      interactionEvents: specialized?.interactionEvents ?? [],
      requiredState: specialized?.requiredState ?? [],
      tokenRefs: specialized?.tokenRefs ?? [
        'ctok:foreground',
        'ctok:background',
        'uiox:token-spacing-4',
      ],
      rules: specialized?.rules ?? [
        `Generate ${type} only with canonical variants, sizes, and semantic design tokens.`,
      ],
    }
  })

  const canonicalTypes = new Set(canonical.map((contract) => contract.type))
  const explicitExtensions = SPECIALIZED_COMPONENT_CONTRACTS.filter(
    (contract) => !canonicalTypes.has(contract.type),
  )
  return [...canonical, ...explicitExtensions]
}

function canonicalValues(
  available: string[] | undefined,
  defaultValue: string | undefined,
): string[] {
  const values = [...new Set([
    ...(available ?? []),
    ...(defaultValue ? [defaultValue] : []),
  ])]
  return values.length > 0 ? values : ['default']
}

export function slugDesignSystemId(state: DesignSystemState): string {
  return `studio-${state.style}-${state.theme}-${state.baseColor}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function buildStudioDesignSystemContract(state: DesignSystemState): StudioDesignSystemContract {
  const componentContracts = buildCanonicalComponentContracts()
  return {
    schemaVersion: '4.0',
    designSystemId: slugDesignSystemId(state),
    componentContract: {
      contractVersion: 'cogo-design-system-contract.v1',
      rendererTargets: RENDERER_TARGETS,
      components: componentContracts,
      tokenRefs: {
        color: ['ctok:background', 'ctok:foreground', 'ctok:card', 'ctok:primary', 'ctok:muted-foreground'],
        typography: ['ctok:font-sans', 'ctok:text-sm', 'ctok:text-base', 'ctok:text-lg'],
        spacing: ['uiox:token-spacing-2', 'uiox:token-spacing-3', 'uiox:token-spacing-4', 'uiox:token-spacing-6'],
        radius: ['ctok:rounded-sm', 'ctok:rounded-md', 'ctok:rounded-lg'],
        elevation: ['ctok:shadow-sm', 'ctok:shadow-md'],
        motion: ['ctok:duration-fast', 'ctok:ease-out'],
      },
      constraints: [
        'Generated Coogo UI must use declared component contracts and semantic token refs.',
        `Component coverage includes all ${Object.keys(CANONICAL_ATOMS).length} canonical Theme V2 atoms; explicit extensions must remain separately identified.`,
        'Refero/Stitch visual inputs may suggest style direction but may not introduce raw values into UI JSON.',
        'Interactive generated apps must prove event binding, ActionFlow execution, state projection, and renderer support.',
        'Corpus admission requires rendered design-quality evidence at or above the design-quality floor.',
      ],
    },
    blockAdmission: BLOCK_ADMISSION_CONTRACT,
  }
}

/**
 * Serialize 6-axis DesignSystemState into full project format.
 *
 * Calls computeThemeTokens to derive oklch CSS vars, radius, and fonts
 * from the preset system. The result is suitable for:
 *   - Embedding in project save JSON (axes for round-trip, cssVars for preview)
 *   - Providing to the generation pipeline as prompt context
 */
export function serializeDesignSystem(state: DesignSystemState): SerializedDesignSystem {
  const tokens = computeThemeTokens(state)
  return {
    axes: { ...state },
    cssVars: tokens.colors ?? {},
    radius: serializeRadius(tokens.radius),
    fonts: tokens.fonts,
    classNameHint: SEMANTIC_CLASSNAME_HINTS,
    designSystemContract: buildStudioDesignSystemContract(state),
  }
}

// ---------------------------------------------------------------------------
// Flat serialization — for cogo-validator app-loader compatibility
// ---------------------------------------------------------------------------

/**
 * Produce a flat Record<string, string> matching the format expected by
 * `cogo-validator/loaders/app-loader.ts` (design-system.json).
 *
 * Keys are CSS variable names without `--` prefix (e.g., "background",
 * "foreground", "primary"). Values are oklch color strings.
 * The "radius" key holds the resolved radius value.
 */
export function toDesignSystemJson(state: DesignSystemState): Record<string, string> {
  const tokens = computeThemeTokens(state)
  return {
    ...(tokens.colors ?? {}),
    radius: serializeRadius(tokens.radius),
  }
}

function serializeRadius(radius: unknown): string {
  if (typeof radius === 'string' && radius.trim()) return radius
  if (radius && typeof radius === 'object' && !Array.isArray(radius)) {
    const record = radius as Record<string, unknown>
    const value = record.lg ?? record.md ?? Object.values(record).find((item) => typeof item === 'string' || typeof item === 'number')
    if (typeof value === 'string' && value.trim()) return value
    if (typeof value === 'number' && Number.isFinite(value)) return `${value}px`
  }
  return '0.5rem'
}
